// This component combines a text input with a dropdown select.
// A dropdown will display adjacent to the focused text input.
// Typing in the text input will filter the displayed options in the dropdown.
// Selecting an option from the dropdown will update the text input's value.
//
// Props
//
// inputClassName: The classes this input should have.
// inputContainerStyle: Configures the input container's appearance. The input elements are nested
//   within a button element, or the 'inputContainer'. This button element corresponds to the
//   'DropdownToggle' component, and is in charge of managing the dropdown. Styling the button
//   element may be needed to make it look like an input element.
// inputName: The 'name' attribute of the input.
// labelDisplayText: What the input label will say.
// labelClassName: The classes this input label should have.
// labelStyle: An object containing CSS properties and values. Configures the label's appearance.
// labelSubText: Explanatory text displayed under the label.
// labelSubTextClassName: The classes this label subtext should have.
// selectHeaderText: The text that should be displayed as a header option in the dropdown select. If
//   no text is supplied here, a header will not be displayed.
// selectMaxOptions: The maximum number of filtered options to display in the dropdown.
// selectOptions: The options in the dropdown select. Each option has these attributes:
//   - displayText: The option's visible text.
//   - searchMatcher: If this attribute is provided, the user's input will filter based on it
//     instead of the option's displayText. Provide this attribute if you want the option to remain
//     in the dropdown if a user types an option's alias.
//     For example: to match 'BHHS' as well as 'Berkshire Hathaway HomeServices', you can provide
//     a searchMatcher of 'BHHS Berkshire Hathaway HomeServices'. Matches are case-insensitive.
//   - value: The value that will be sent by the form. Should be unique.

import escapeRegExp from "lodash/escapeRegExp";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Label, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";

const propTypes = {
  inputClassName: PropTypes.string,
  inputContainerStyle: PropTypes.object,
  inputName: PropTypes.string.isRequired,
  inputPlaceholder: PropTypes.string,
  labelDisplayText: PropTypes.string,
  labelClassName: PropTypes.string,
  labelStyle: PropTypes.object,
  labelSubText: PropTypes.string,
  labelSubTextClassName: PropTypes.string,
  selectHeaderText: PropTypes.string,
  selectMaxOptions: PropTypes.number,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.string.isRequired,
      searchMatcher: PropTypes.string,
      value: PropTypes.string.isRequired
    })
  ).isRequired
};

const FilterSelect = props => {
  const {
    inputClassName = "",
    inputName,
    inputPlaceholder = "",
    labelDisplayText = "",
    labelClassName = "",
    labelSubText = "",
    labelSubTextClassName = "small",
    selectHeaderText = "",
    selectMaxOptions = 7,
    selectOptions
  } = props;

  const inputContainerStyle = Object.assign(
    {},
    { all: "initial", width: "100%" },
    props.inputContainerStyle
  );
  const labelStyle = Object.assign({}, { width: "100%" }, props.labelStyle);

  const [filteredOptions, setFilteredOptions] = useState(selectOptions);
  const [inputText, setInputText] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const applyFilterToOptions = filterString => {
    if (filterString) {
      setFilteredOptions(optionsMatching(filterString));
    } else {
      setFilteredOptions(selectOptions);
    }
  };

  const filteredOptionsDisplayed = () => {
    if (selectMaxOptions) {
      return filteredOptions.slice(0, selectMaxOptions);
    } else {
      return filteredOptions;
    }
  };

  const handleInputChange = event => {
    const text = event.target.value;
    setInputText(text);
    applyFilterToOptions(text);
    setDropdownOpen(true);
  };

  const handleSelect = event => {
    setSelectedValue(event.target.value);
    setInputText(event.target.textContent);
  };

  const labelSubTextContainer = () => {
    if (labelSubText && labelSubText.length > 0) {
      return <div className={labelSubTextClassName}>{labelSubText}</div>;
    }
  };

  const numberOfOptionsNotDisplayed = () => {
    return filteredOptions.length - filteredOptionsDisplayed().length;
  };

  const optionsFooter = () => {
    if (numberOfOptionsNotDisplayed() > 0) {
      return <DropdownItem disabled>{`${numberOfOptionsNotDisplayed()} more`}</DropdownItem>;
    }
  };

  const optionsHeader = () => {
    if (selectHeaderText && selectHeaderText.length > 0) {
      return <DropdownItem header>{selectHeaderText}</DropdownItem>;
    }
  };

  const optionsMatching = filterString => {
    return selectOptions.filter(option => {
      const filterRegExp = new RegExp(escapeRegExp(filterString), "i");
      const matcher = option.searchMatcher || option.displayText;

      return matcher.match(filterRegExp);
    });
  };

  const toggle = () => setDropdownOpen(prevState => !prevState);

  return (
    <Label className={labelClassName} for={inputName} style={labelStyle}>
      {labelDisplayText}
      {labelSubTextContainer()}
      <Dropdown isOpen={dropdownOpen && filteredOptions.length > 0} toggle={toggle}>
        <DropdownToggle style={inputContainerStyle} title="filter-select-dropdown">
          <Input type="hidden" name={inputName} value={selectedValue} />
          <Input
            type="text"
            name={`${inputName}-search`}
            className={inputClassName}
            onChange={handleInputChange}
            value={inputText}
            placeholder={inputPlaceholder}
          />
        </DropdownToggle>
        <DropdownMenu flip={false}>
          {optionsHeader()}
          {filteredOptionsDisplayed().map(option => {
            return (
              <DropdownItem key={option.value} value={option.value} onClick={handleSelect}>
                {option.displayText}
              </DropdownItem>
            );
          })}
          {optionsFooter()}
        </DropdownMenu>
      </Dropdown>
    </Label>
  );
};

FilterSelect.propTypes = propTypes;

export default FilterSelect;
